import {
  AGE_CATEGORIES,
  ANAESTHETIC_TYPES,
  ASA_GRADES,
  DAY_CASE_OPTIONS,
  FUNDING_OPTIONS,
  POST_OP_DESTINATIONS,
  ROLES,
  SESSIONS,
  SPECIALTIES,
  SUPERVISOR_GRADES,
  URGENCIES,
} from "../case/catalogues";
import type { AnalyticsRecord } from "./analytics-record";
import type {
  DimensionDefinition,
  DimensionKey,
  DimensionValue,
  FilterOperator,
  VisualisationType,
} from "./report-definition";

const CATEGORY_FILTERS = ["is", "is_not", "is_any_of", "is_known", "is_unknown"] as const satisfies readonly FilterOperator[];
const DATE_FILTERS = [
  "is",
  "is_not",
  "before",
  "after",
  "between",
  "in_the_last",
  "calendar_year",
  "training_year",
  "is_known",
  "is_unknown",
] as const satisfies readonly FilterOperator[];
const PERIOD_FILTERS = [
  "is",
  "is_not",
  "before",
  "after",
  "is_known",
  "is_unknown",
] as const satisfies readonly FilterOperator[];
const CATEGORY_VISUALISATIONS = ["vertical_bar", "horizontal_bar", "donut", "table"] as const satisfies readonly VisualisationType[];
const DATE_VISUALISATIONS = ["line", "vertical_bar", "table"] as const satisfies readonly VisualisationType[];

const WEEKDAY_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

const SUPERVISION_ORDER = [
  "1. Direct supervisor involvement",
  "2a. Supervisor in theatre suite",
  "2b. Supervisor within hospital",
  "3. Supervisor on call from home",
  "4. Able to manage independently",
] as const;

type AnalyticsDimensionDefinition = DimensionDefinition<AnalyticsRecord>;

function category(
  definition: Omit<AnalyticsDimensionDefinition, "dataType" | "filterOperations" | "suitableVisualisations" | "unknownLabel">,
): AnalyticsDimensionDefinition {
  return {
    ...definition,
    dataType: "category",
    filterOperations: CATEGORY_FILTERS,
    suitableVisualisations: CATEGORY_VISUALISATIONS,
    unknownLabel: "Not recorded",
  };
}

function dateDimension(
  definition: Omit<AnalyticsDimensionDefinition, "dataType" | "filterOperations" | "suitableVisualisations" | "unknownLabel">,
  filterOperations: readonly FilterOperator[] = DATE_FILTERS,
): AnalyticsDimensionDefinition {
  return {
    ...definition,
    dataType: "date",
    filterOperations,
    suitableVisualisations: DATE_VISUALISATIONS,
    unknownLabel: "Not recorded",
  };
}

export const DIMENSION_REGISTRY = {
  case_date: dateDimension({
    key: "case_date",
    label: "Case date",
    description: "The recorded clinical date of the case.",
    sourceField: "date",
    derive: (record) => record.caseDate,
    mayGroupPrimary: true,
    mayGroupSecondary: false,
  }),
  calendar_year: dateDimension({
    key: "calendar_year",
    label: "Year",
    description: "Calendar year derived from the case date.",
    derive: (record) => record.calendarYear,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }, PERIOD_FILTERS),
  calendar_month: dateDimension({
    key: "calendar_month",
    label: "Month",
    description: "Calendar month and year derived from the case date.",
    derive: (record) => record.calendarMonth,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }, PERIOD_FILTERS),
  training_year: dateDimension({
    key: "training_year",
    label: "Training year",
    description: "August to July training year derived from the case date.",
    derive: (record) => record.trainingYear,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }, PERIOD_FILTERS),
  day_of_week: category({
    key: "day_of_week",
    label: "Day of week",
    description: "Day of the week derived from the case date.",
    derive: (record) => record.dayOfWeek,
    categoryOrder: WEEKDAY_ORDER,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  session: category({
    key: "session",
    label: "Session",
    description: "The recorded theatre session.",
    sourceField: "session",
    derive: (record) => record.session,
    categoryOrder: SESSIONS,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  specialty: category({
    key: "specialty",
    label: "Specialty",
    description: "The canonical clinical specialty for the case.",
    sourceField: "data.spec",
    derive: (record) => record.specialty,
    categoryOrder: SPECIALTIES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  procedure: category({
    key: "procedure",
    label: "Procedure",
    description: "The operation or case name recorded in the logbook.",
    sourceField: "data.procedure",
    derive: (record) => record.procedure,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  patient_age: category({
    key: "patient_age",
    label: "Age as recorded",
    description: "The original non-identifiable age value recorded for the case.",
    sourceField: "data.age",
    derive: (record) => record.patientAge,
    mayGroupPrimary: true,
    mayGroupSecondary: false,
  }),
  age_group: category({
    key: "age_group",
    label: "Age group",
    description: "The standard LogBookAnaes clinical age group.",
    sourceField: "data.age",
    derive: (record) => record.ageGroup,
    categoryOrder: AGE_CATEGORIES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  asa: category({
    key: "asa",
    label: "ASA",
    description: "The recorded ASA physical status classification.",
    sourceField: "data.asa",
    derive: (record) => record.asa,
    categoryOrder: ASA_GRADES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  urgency: category({
    key: "urgency",
    label: "Urgency",
    description: "The NCEPOD urgency recorded for the case.",
    sourceField: "data.urg",
    derive: (record) => record.urgency,
    categoryOrder: URGENCIES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  supervision_level: category({
    key: "supervision_level",
    label: "Supervision",
    description: "The canonical supervision level for the case.",
    sourceField: "data.sup",
    derive: (record) => record.supervisionLevel,
    categoryOrder: SUPERVISION_ORDER,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  hospital: category({
    key: "hospital",
    label: "Hospital or site",
    description: "The hospital or clinical site recorded for the case.",
    sourceField: "hospital",
    derive: (record) => record.hospital,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  funding: category({
    key: "funding",
    label: "Funding",
    description: "The detailed funding category recorded for the case.",
    sourceField: "data.funding",
    derive: (record) => record.funding,
    categoryOrder: FUNDING_OPTIONS,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  nhs_private: category({
    key: "nhs_private",
    label: "NHS or private",
    description: "A simple NHS or private grouping derived from funding.",
    sourceField: "data.funding",
    derive: (record) => record.nhsPrivate,
    categoryOrder: ["NHS", "Private", "Other"],
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  anaesthetic_technique: category({
    key: "anaesthetic_technique",
    label: "Anaesthetic technique",
    description: "The principal anaesthetic technique recorded for the case.",
    sourceField: "data.maint",
    derive: (record) => record.anaestheticTechnique,
    categoryOrder: ANAESTHETIC_TYPES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  airway_technique: category({
    key: "airway_technique",
    label: "Airway technique",
    description: "Each airway technique recorded for the case.",
    sourceField: "data.airway",
    derive: (record) => record.airwayTechnique,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
    isMultiValue: true,
  }),
  regional_technique: category({
    key: "regional_technique",
    label: "Regional technique",
    description: "Each regional anaesthetic technique recorded for the case.",
    sourceField: "data.regional",
    derive: (record) => record.regionalTechnique,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
    isMultiValue: true,
  }),
  role: category({
    key: "role",
    label: "Role",
    description: "The anaesthetist's recorded role in the case.",
    sourceField: "data.role",
    derive: (record) => record.role,
    categoryOrder: ROLES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  supervisor_grade: category({
    key: "supervisor_grade",
    label: "Supervisor grade",
    description: "The grade of the recorded supervisor.",
    sourceField: "data.supGrade",
    derive: (record) => record.supervisorGrade,
    categoryOrder: SUPERVISOR_GRADES,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  complications: category({
    key: "complications",
    label: "Complication",
    description: "Each complication recorded for the case.",
    sourceField: "data.complications",
    derive: (record) => record.complications,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
    isMultiValue: true,
  }),
  day_case: category({
    key: "day_case",
    label: "Day case",
    description: "Whether the case was recorded as a day case.",
    sourceField: "data.dayCase",
    derive: (record) => record.dayCase,
    categoryOrder: DAY_CASE_OPTIONS,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
  practical_procedure: category({
    key: "practical_procedure",
    label: "Practical procedure",
    description: "Each access or practical procedure recorded during the case.",
    sourceField: "data.access_new",
    derive: (record) => record.practicalProcedure,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
    isMultiValue: true,
  }),
  postoperative_destination: category({
    key: "postoperative_destination",
    label: "Postoperative destination",
    description: "The recorded destination after anaesthesia.",
    sourceField: "data.postop",
    derive: (record) => record.postoperativeDestination,
    categoryOrder: POST_OP_DESTINATIONS,
    mayGroupPrimary: true,
    mayGroupSecondary: true,
  }),
} as const satisfies Readonly<Record<DimensionKey, AnalyticsDimensionDefinition>>;

export function getDimensionDefinition(key: DimensionKey): AnalyticsDimensionDefinition {
  return DIMENSION_REGISTRY[key];
}

/** Returns one null for a missing scalar or empty multi-value field. */
export function dimensionValues(record: AnalyticsRecord, key: DimensionKey): readonly DimensionValue[] {
  const derived = DIMENSION_REGISTRY[key].derive?.(record) ?? null;
  if (Array.isArray(derived)) {
    const values = derived as readonly DimensionValue[];
    // No regional technique means there is no category to plot. Returning an
    // empty set keeps absence out of regional charts while `is_unknown` and
    // missing-value counts still correctly recognise the record as unrecorded.
    if (key === "regional_technique" && values.length === 0) return [];
    return values.length > 0 ? [...new Set(values)] : [null];
  }
  return [derived as DimensionValue];
}

export function dimensionLabel(key: DimensionKey, value: DimensionValue): string {
  if (value === null || value === "") return DIMENSION_REGISTRY[key].unknownLabel;
  if (key === "calendar_month" && typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
    const [year = "", month = ""] = value.split("-");
    const monthIndex = Number(month) - 1;
    if (monthIndex >= 0 && monthIndex < 12) {
      const label = new Intl.DateTimeFormat("en-GB", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(Date.UTC(Number(year), monthIndex, 1)));
      return label;
    }
  }
  if (key === "training_year" && typeof value === "string" && /^\d{4}-\d{2}$/.test(value)) {
    const startYear = Number(value.slice(0, 4));
    return `${startYear}/${String((startYear + 1) % 100).padStart(2, "0")}`;
  }
  return String(value);
}

function categoryRank(key: DimensionKey, value: DimensionValue): number {
  if (value === null) return Number.MAX_SAFE_INTEGER;
  const order = DIMENSION_REGISTRY[key].categoryOrder;
  const rank = order?.indexOf(String(value));
  return rank === undefined || rank < 0 ? Number.MAX_SAFE_INTEGER - 1 : rank;
}

export function compareDimensionValues(
  key: DimensionKey,
  left: DimensionValue,
  right: DimensionValue,
): number {
  if (left === null && right !== null) return 1;
  if (right === null && left !== null) return -1;
  if (
    DIMENSION_REGISTRY[key].dataType === "date" &&
    typeof left === "string" &&
    typeof right === "string"
  ) {
    return left.localeCompare(right, "en-GB", { numeric: true });
  }
  const rankDifference = categoryRank(key, left) - categoryRank(key, right);
  if (rankDifference !== 0) return rankDifference;
  return dimensionLabel(key, left).localeCompare(dimensionLabel(key, right), "en-GB", {
    numeric: true,
    sensitivity: "base",
  });
}

export function isDateDimension(key: DimensionKey): boolean {
  return DIMENSION_REGISTRY[key].dataType === "date";
}
